import { standardCollator } from 'js/utils/string';

/**
 * Build per-capability-element scores from latest user-group assessments.
 *
 * Metrics are keyed as metrics[elementKey][capabilityId] (from getCapabilityData).
 * For each capability × element:
 * 1. Take that element's scores from each selected UGA
 * 2. Mean across groups' normalizedScore; min/max across groups' minScore/maxScore
 *
 * Parents never inherit scores from children. Element keys are never averaged together.
 */

function mean(values) {
  if (!values.length) {
    return null;
  }
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function finiteNumber(value) {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * @returns {Record<string, Record<string, { score: number, minScore: number|null, maxScore: number|null, groupCount: number }>>}
 */
export function buildCapabilityElementScores({
  userGroupAssessments,
  metricsByUgaId,
}) {
  // scores[capabilityId][elementKey] = { means: [], mins: [], maxes: [] }
  const scores = {};

  (userGroupAssessments || []).forEach((uga) => {
    const ugaMetrics = metricsByUgaId?.[uga.id];
    if (!ugaMetrics?.metrics) {
      return;
    }

    Object.entries(ugaMetrics.metrics).forEach(([elementKey, byCapability]) => {
      Object.entries(byCapability || {}).forEach(
        ([capabilityId, questionMetrics]) => {
          const score = finiteNumber(questionMetrics?.normalizedScore);
          if (score == null) {
            return;
          }
          if (!scores[capabilityId]) {
            scores[capabilityId] = {};
          }
          if (!scores[capabilityId][elementKey]) {
            scores[capabilityId][elementKey] = {
              means: [],
              mins: [],
              maxes: [],
            };
          }
          const bucket = scores[capabilityId][elementKey];
          bucket.means.push(score);
          const minScore = finiteNumber(questionMetrics?.minScore);
          const maxScore = finiteNumber(questionMetrics?.maxScore);
          if (minScore != null) {
            bucket.mins.push(minScore);
          }
          if (maxScore != null) {
            bucket.maxes.push(maxScore);
          }
        }
      );
    });
  });

  return Object.fromEntries(
    Object.entries(scores).map(([capabilityId, byElement]) => [
      capabilityId,
      Object.fromEntries(
        Object.entries(byElement).map(([elementKey, bucket]) => [
          elementKey,
          {
            score: mean(bucket.means),
            minScore: bucket.mins.length ? Math.min(...bucket.mins) : null,
            maxScore: bucket.maxes.length ? Math.max(...bucket.maxes) : null,
            groupCount: bucket.means.length,
          },
        ])
      ),
    ])
  );
}

/** Chart palettes start at 2 to match capability score bars. */
const CAPABILITY_CHART_PALETTE_OFFSET = 2;

const PINNED_ELEMENT_NAMES = new Set(['health', 'satisfaction']);

function pinnedElementRank(element) {
  const name = (element?.name || '').trim().toLowerCase();
  const key = (element?.key || '').trim().toLowerCase();
  return PINNED_ELEMENT_NAMES.has(name) || PINNED_ELEMENT_NAMES.has(key)
    ? 0
    : 1;
}

export function compareCapabilityElements(a, b, collator = standardCollator) {
  const pinDiff = pinnedElementRank(a) - pinnedElementRank(b);
  if (pinDiff !== 0) {
    return pinDiff;
  }

  if (pinnedElementRank(a) === 0) {
    return collator.compare(a.name || a.key, b.name || b.key);
  }

  const aIdx = a.paletteIndex ?? Number.MAX_SAFE_INTEGER;
  const bIdx = b.paletteIndex ?? Number.MAX_SAFE_INTEGER;
  if (aIdx !== bIdx) {
    return aIdx - bIdx;
  }
  return collator.compare(a.name || a.key, b.name || b.key);
}

/**
 * Sort capability elements and assign palette-chart indexes used by
 * overview bars, treemap buttons, and tooltips.
 */
export function buildCapabilityElementChartOptions(
  capabilityElements,
  collator = standardCollator
) {
  const elements = Object.values(capabilityElements || {});
  const sorted = [...elements].sort((a, b) =>
    compareCapabilityElements(a, b, collator)
  );

  return sorted.map((el, i) => ({
    ...el,
    paletteIndex:
      typeof el.paletteIndex === 'number'
        ? el.paletteIndex
        : i + CAPABILITY_CHART_PALETTE_OFFSET,
  }));
}

/** Prefer Health, then Satisfaction (name or key); otherwise first after shared sort. */
export function pickDefaultElementKey(capabilityElements) {
  const elements = Array.isArray(capabilityElements)
    ? [...capabilityElements]
    : Object.values(capabilityElements || {});
  if (!elements.length) {
    return null;
  }

  return [...elements].sort((a, b) => compareCapabilityElements(a, b))[0].key;
}

/** True when both id lists contain the same members (order-independent). */
export function sameIdSet(a = [], b = []) {
  if (a.length !== b.length) {
    return false;
  }
  const setB = new Set(b);
  return a.every((id) => setB.has(id));
}

/** Capability IDs that have a score on any element key. */
export function capabilitiesWithAnyScores(scoresByCapability) {
  return Object.entries(scoresByCapability || {})
    .filter(([, byElement]) =>
      Object.values(byElement || {}).some(
        (entry) => finiteNumber(entry?.score) != null
      )
    )
    .map(([capabilityId]) => capabilityId);
}

/** Flatten capability tree for combobox display (tree order). */
export function flattenCapabilityTree(
  capabilities,
  depth = 0,
  acc = [],
  path = []
) {
  (capabilities || []).forEach((capability) => {
    const nextPath = [...path, capability.name];
    acc.push({
      id: capability.id,
      name: capability.name,
      depth,
      path: nextPath,
      capability,
    });
    flattenCapabilityTree(capability.children, depth + 1, acc, nextPath);
  });
  return acc;
}

/**
 * Keep selected nodes and ancestors of selected nodes for treemap nesting.
 */
export function pruneCapabilityTree(capabilities, selectedIds) {
  const selected = new Set(selectedIds || []);

  function prune(nodes) {
    return (nodes || [])
      .map((node) => {
        const children = prune(node.children);
        const keep = selected.has(node.id) || (children && children.length > 0);
        if (!keep) {
          return null;
        }
        return {
          ...node,
          children,
        };
      })
      .filter(Boolean);
  }

  return prune(capabilities);
}

/** Selected capabilities in tree order (for bar grids). */
export function selectedCapabilitiesInTreeOrder(capabilities, selectedIds) {
  const selected = new Set(selectedIds || []);
  return flattenCapabilityTree(capabilities)
    .filter((item) => selected.has(item.id))
    .map((item) => ({
      ...item.capability,
      depth: item.depth,
      path: item.path,
    }));
}

export function countCapabilityDescendants(capability) {
  const children = capability?.children || [];
  return children.reduce(
    (count, child) => count + 1 + countCapabilityDescendants(child),
    0
  );
}

export function countVisibleCapabilityNodes(capabilities) {
  return (capabilities || []).reduce(
    (count, capability) =>
      count + 1 + countVisibleCapabilityNodes(capability.children),
    0
  );
}

/**
 * Attach areaWeight and the selected element's score onto a capability tree.
 */
export function annotateCapabilityTree(
  capabilities,
  scoresByCapability,
  selectedElementKey,
  tileArea
) {
  return (capabilities || []).map((capability) => {
    const children = annotateCapabilityTree(
      capability.children,
      scoresByCapability,
      selectedElementKey,
      tileArea
    );
    const descendantCount = countCapabilityDescendants({
      ...capability,
      children: capability.children,
    });
    const overview =
      selectedElementKey != null
        ? scoresByCapability?.[capability.id]?.[selectedElementKey]
        : null;

    return {
      ...capability,
      children,
      areaWeight: tileArea * (1 + descendantCount),
      score: overview?.score ?? null,
      groupCount: overview?.groupCount ?? 0,
    };
  });
}

/** Parse a 0–100 display value into a stored 0–1 threshold. Invalid input becomes 0. */
export function displayThresholdToStored(raw) {
  const parsed =
    typeof raw === 'number' ? raw : parseFloat(String(raw ?? '').trim());
  const display = Number.isFinite(parsed) ? parsed : 0;
  const clamped = Math.min(100, Math.max(0, display));
  return clamped / 100;
}

/** Convert a stored 0–1 threshold to a 0–100 display string. */
export function storedThresholdToDisplay(value) {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return '';
  }
  const display = value * 100;
  return Number.isInteger(display)
    ? String(display)
    : String(parseFloat(display.toPrecision(12)));
}

/** Normalize a thresholds map entry to `{ value, enabled }`. */
export function parseStoredThreshold(entry) {
  if (entry && typeof entry === 'object') {
    const value = finiteNumber(entry.value);
    return {
      value: value ?? 0,
      enabled: entry.enabled === true,
    };
  }
  return { value: 0, enabled: false };
}

/** Value to draw on charts; null when the threshold is missing or disabled. */
export function enabledThresholdValue(entry) {
  if (entry == null) {
    return null;
  }
  const parsed = parseStoredThreshold(entry);
  return parsed.enabled ? parsed.value : null;
}
